import { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import Linkify from 'linkify-react'

import MoreIcon from '@assets/images/three-dot-icon.svg?react'
import TrashIcon from '@assets/images/trash-icon.svg?react'

import { Comment, RemoteFile, User } from '@types'

import { getFileUrl } from '@utils/getFileUrl'

import { useMe } from '@hooks/user/me.hook'
import { useDeleteComment } from '@hooks/comment/delete-comment.hook'

import { UserBadge } from '@components/UserBadge'
import { Action, DropDownAction } from '@components/DropDownAction'
import { Loader } from '@components/Loader'

import {
  Container,
  FilePreview,
  FilePreviewContainer,
  HeaderContainer,
  LabelDate,
  MoreIconContainer,
  TrashIconContainer,
  Value,
} from './CommentRow.styled'

interface Props {
  comment: Comment
  readOnly?: boolean
}

export const CommentRow = (props: Props): JSX.Element => {
  const {
    comment,
    comment: { author, createdDate, value, file, url: fileUrl },
    readOnly = false,
  } = props

  const { t } = useTranslation()

  const [photos, setPhotos] = useState<RemoteFile[]>([])

  const loadPhotoPreview = useCallback(async () => {
    if (file && fileUrl) {
      const url = file.downloadLink || (await getFileUrl(fileUrl))

      const remoteFile: RemoteFile = {
        id: file.id,
        deleted: false,
        name: file.name,
        type: file.type,
        extension: file.extension,
        url,
      }
      setPhotos([remoteFile])
    } else {
      setPhotos([])
    }
  }, [file, fileUrl])

  useEffect(() => {
    loadPhotoPreview()
  }, [loadPhotoPreview])

  const { deleteComment } = useDeleteComment()

  const actions: Action[] = [
    {
      key: 'delete',
      label: t('modules_main_components_SideBarMenu_label_delete_project_button'),
      Icon: () => (
        <TrashIconContainer>
          <TrashIcon />
        </TrashIconContainer>
      ),
    },
  ]

  const onClickAction = (action: Action) => {
    if (action.key !== 'delete') return

    deleteComment(comment.id)
  }

  return (
    <Container>
      <HeaderContainer>
        <UserBadge size="small" user={author} displayLabel />
        <LabelDate>{moment(createdDate).fromNow()}</LabelDate>
        {!readOnly && <MeDropDownAction author={author} actions={actions} onClickAction={onClickAction} />}
      </HeaderContainer>

      {fileUrl && (
        <FilePreviewContainer
          onClick={async () => {
            const url = await getFileUrl(fileUrl)
            window.open(url, '_blank')
          }}
        >
          {photos[0] ? <FilePreview src={photos[0].url} /> : <Loader size={14} />}
        </FilePreviewContainer>
      )}
      <Value>
        <Linkify options={{ target: '_blank' }}>{value}</Linkify>
      </Value>
    </Container>
  )
}

interface MeDropDownActionProps {
  author: User
  actions: Action[]
  onClickAction: (action: Action) => void
}

const MeDropDownAction = (props: MeDropDownActionProps): JSX.Element | null => {
  const { author, actions, onClickAction } = props

  const { getMe } = useMe()

  const meIsAuthor = getMe().id == author.id
  if (!meIsAuthor) {
    return null
  }

  return (
    <DropDownAction
      mask="light"
      sections={[{ key: '', actions }]}
      onClickAction={onClickAction}
      WrappedComponent={() => (
        <MoreIconContainer>
          <MoreIcon />
        </MoreIconContainer>
      )}
    />
  )
}
